import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeProvider';
import axios from 'axios';
import { Upload, BarChart3, Trash2, Clock, Trophy, TrendingUp, Users, Zap, ChevronDown, ChevronRight, AlertTriangle, LogOut, User, Crown, Lock, Cloud, Star, Timer, Shield, Gamepad2, Radio, FileText, Activity } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import RaceList from './RaceList';
import RaceComparison from './RaceComparison';
import MultiSessionComparison from './MultiSessionComparison';
import DriveImporter from './DriveImporter';
import MyCategoriesManager from './MyCategoriesManager';
import SpeedhiveImporter from './SpeedhiveImporter';
import SpeedhiveSessionsExplorer from './SpeedhiveSessionsExplorer';
import FavoritesDashboard from './FavoritesDashboard';
import NotificationBell from './NotificationBell';
import Temporizador from './Temporizador';
import ACTCLive from './ACTCLive';
// Kept imported for a future dedicated /actc-live route (feature retired
// from the hub in favour of the Speedhive-powered "Sesiones en Vivo").
export const _RETIRED_ACTCLive = ACTCLive;
import { API } from '../config/api';

// Admin emails list (must match backend)
const ADMIN_EMAILS = [
  "admin@datapro.com",
  "soporte@datapro.com",
  "demo@datapro.com"
];

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const logoSrc = theme === 'dark' ? '/datapro-logo-white.png' : '/datapro-logo.png';
  const [races, setRaces] = useState([]);
  // `null` means "haven't tried yet"; `true` after the first successful
  // (or failed) fetchRaces(). Prevents the "No hay carreras disponibles"
  // empty-state card from flashing on fresh mounts (e.g. coming back
  // from /live) BEFORE the first /api/races response resolves.
  const [racesLoaded, setRacesLoaded] = useState(false);
  const [selectedRace, setSelectedRace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [activeTab, setActiveTab] = useState('races');
  // Sub-tab selector inside the "cloud" import tab: switches between
  // Google Drive (planillas oficiales publicadas por la categoría) and
  // Speedhive (feed directo de MyLaps con sesiones ya finalizadas).
  // 'speedhive' is the primary flow (live-timing auto-capture from MyLaps).
  // 'drive' is kept as an opt-in fallback for events where the class hosts
  // the official spreadsheets in Google Drive rather than using MyLaps.
  const [cloudSource, setCloudSource] = useState('speedhive');
  const [showMultiSessionComparison, setShowMultiSessionComparison] = useState(false);
  const [showOfficialImport, setShowOfficialImport] = useState(false);
  const [showMyCategories, setShowMyCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('auto');

  const isPro = user?.plan === 'pro' || user?.plan === 'simracing';
  const isSimracing = user?.plan === 'simracing';
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
  // Pit Engineer access: explicit `pit_access` flag OR is_admin OR legacy admin email.
  const hasPitAccess = !!user?.pit_access || !!user?.is_admin || isAdmin;

  useEffect(() => {
    fetchRaces();
    // Refresh current user so expired subscriptions are downgraded server-side
    // and the UI reflects the real plan (not cached state from login).
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (res.data && setUser) setUser(res.data);
      } catch {
        // Not authenticated — routing elsewhere handles this.
      }
    })();
  }, []);

  // Live refresh for the Race List.
  // Before this hook `fetchRaces()` only ran on mount, so once an
  // AutoLive capture produced a new tanda (or renamed an in-progress
  // one as Speedhive published the stable name post-rotation) the
  // user had to F5 manually to see the correction. We now re-poll
  // every 15 s while ANY AutoLive capture is still in progress, and
  // every 60 s otherwise so a brand-new tanda surfaces reasonably
  // fast without being a battery hog on an idle dashboard. The poll
  // auto-pauses when the browser tab is hidden.
  const hasInProgress = races.some(
    (r) => r.source === 'speedhive_live_auto' && r.status === 'in_progress'
  );
  useEffect(() => {
    if (activeTab !== 'races') return undefined;
    if (racesSigRef.current) fetchRaces();
    const intervalMs = hasInProgress ? 15000 : 60000;
    const tick = () => {
      if (document.visibilityState === 'visible') fetchRaces();
    };
    const id = setInterval(tick, intervalMs);
    const onVis = () => {
      // Refresh immediately when the user comes back to the tab —
      // otherwise they'd see a stale list for up to 60 s.
      if (document.visibilityState === 'visible') fetchRaces();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [hasInProgress, activeTab]);

  const racesSigRef = useRef('');
  const fetchRaces = async () => {
    try {
      const response = await axios.get(`${API}/races`, { withCredentials: true });
      const sig = JSON.stringify(response.data);
      if (sig !== racesSigRef.current) {
        racesSigRef.current = sig;
        setRaces(response.data);
      }
    } catch (error) {
      console.error('Error fetching races:', error);
      toast.error('Error al cargar las carreras');
    } finally {
      setRacesLoaded(true);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('datapro_session_token');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('datapro_session_token');
      navigate('/');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check plan limits
    if (!isPro && races.length >= 1) {
      toast.error('Plan gratuito permite solo 1 sesión. Actualiza a PRO para más.');
      event.target.value = '';
      return;
    }

    const validExtensions = ['.pdf', '.xls', '.xlsx', '.html'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validExtensions.includes(fileExt)) {
      toast.error('Por favor sube un archivo PDF, XLS, XLSX o HTML');
      return;
    }

    setUploadProgress(true);

    // For Rotax VXV HTML exports we prefer the <title> tag over the raw
    // filename — MyLaps names the export cleanly ("Laptimes of MASTER MAX
    // ..." → "MASTER MAX ..."), saving the user manual typing. Fallback
    // is the filename stem for non-HTML files or malformed HTML.
    let raceName = file.name.replace(/\.(pdf|xls|xlsx|html)$/i, '');
    if (fileExt === '.html') {
      try {
        const htmlText = await file.text();
        const parsed = new DOMParser().parseFromString(htmlText, 'text/html');
        const rawTitle = (parsed.querySelector('title')?.textContent || '').trim();
        if (rawTitle) {
          // Strip common MyLaps prefixes (ES + EN).
          const stripped = rawTitle
            .replace(/^\s*(Laptimes of|Lap\s*times of|Tiempos de vueltas de)\s*/i, '')
            .trim();
          if (stripped) raceName = stripped;
        }
      } catch (_) {
        // Ignore parse errors; keep filename stem as fallback.
      }
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', raceName);
    formData.append('date', new Date().toISOString().split('T')[0]);
    
    // Add category if not auto-detect
    if (selectedCategory !== 'auto') {
      formData.append('category', selectedCategory);
    }

    try {
      const response = await axios.post(`${API}/races/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const categoryLabel = response.data.category === 'turismo_pista' ? 'Turismo Pista' : 
                           response.data.category === 'carshow_santafesino' ? 'CarShow Santafesino' :
                           response.data.category === 'turismo_nacional' ? 'Turismo Nacional' :
                           response.data.category === 'rotax' ? 'ROTAX Karting' : '';
      toast.success(`✓ Carrera creada: ${response.data.driver_count} pilotos extraídos ${categoryLabel ? `(${categoryLabel})` : ''}`);
      toast.warning(
        'Si encontrás un error en los datos, es debido al cronometraje oficial del evento.',
        { duration: 8000, id: 'cronometraje-warning' }
      );
      await fetchRaces();
      setActiveTab('races');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar el archivo');
    } finally {
      setUploadProgress(false);
      event.target.value = '';
    }
  };

  const handleDeleteRace = async (raceId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta carrera?')) return;

    try {
      await axios.delete(`${API}/races/${raceId}`);
      toast.success('✓ Carrera eliminada correctamente');
      await fetchRaces();
      if (selectedRace?.id === raceId) {
        setSelectedRace(null);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Error al eliminar la carrera');
    }
  };

  const handleClearAllRaces = async () => {
    if (!isPro) {
      toast.error('Función exclusiva del plan PRO');
      return;
    }
    
    if (races.length === 0) {
      toast.info('No hay sesiones para eliminar');
      return;
    }
    
    if (!window.confirm(`¿Estás seguro de eliminar TODAS las ${races.length} sesiones? Esta acción no se puede deshacer.`)) return;

    try {
      await axios.delete(`${API}/races/clear-all`, { withCredentials: true });
      toast.success(`✓ ${races.length} sesiones eliminadas`);
      await fetchRaces();
      setSelectedRace(null);
    } catch (error) {
      console.error('Clear all error:', error);
      toast.error('Error al eliminar las sesiones');
    }
  };

  const handleSelectRace = async (race) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/races/${race.id}`, { withCredentials: true });
      setSelectedRace(response.data);
      setActiveTab('compare');
    } catch (error) {
      toast.error('Error al cargar los detalles de la carrera');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header with DATAPRO logo and user info — DEMO Fase 2 (migrated to --dp-* + t()) */}
      <div
        data-testid="dashboard-brand-header"
        className="relative"
        style={{
          backgroundColor: 'var(--dp-surface-0)',
          borderBottom: '4px solid var(--dp-primary)',
        }}
      >
        <div className="container mx-auto px-3 md:px-6 py-2 md:py-3">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <Link to="/" className="flex items-center gap-2 md:gap-4 hover:opacity-90 transition-opacity cursor-pointer min-w-0">
              <img 
                src={logoSrc}
                alt="DATAPRO Racing" 
                data-testid="dashboard-brand-logo"
                className="h-10 md:h-20 lg:h-28 w-auto object-contain flex-shrink-0"
              />
              <div
                className="hidden lg:block pl-4 min-w-0"
                style={{ borderLeft: '2px solid var(--dp-primary)' }}
              >
                <h1
                  data-testid="dashboard-brand-subtitle"
                  className="text-lg md:text-xl font-bold tracking-tight truncate"
                  style={{ color: 'var(--dp-text-primary)' }}
                >
                  {t('nav.brand_subtitle')}
                </h1>
                <p
                  data-testid="dashboard-brand-tagline"
                  className="text-xs md:text-sm truncate"
                  style={{ color: 'var(--dp-text-secondary)' }}
                >
                  {t('nav.brand_tagline')}
                </p>
              </div>
            </Link>
            
            {/* User info and logout */}
            <div className="flex items-center gap-1 md:gap-4">
              {user && (
                <>
                  {isPro ? (
                    <span className="flex items-center gap-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs font-bold px-2 md:px-3 py-1 rounded-full">
                      <Crown className="w-3 h-3" />
                      <span className="hidden sm:inline">PRO</span>
                    </span>
                  ) : (
                    <Link to="/pricing" className="hidden sm:block">
                      <Button size="sm" variant="outline" className="text-red-600 border-red-600 hover:bg-red-50 h-7 md:h-8 text-xs md:text-sm px-2 md:px-3">
                        <Zap className="w-3 h-3 mr-1" />
                        <span className="hidden md:inline">Upgrade</span>
                      </Button>
                    </Link>
                  )}
                  
                  {/* Simracing Button */}
                  <Link to="/simracing" className="hidden sm:block">
                    <Button 
                      size="sm" 
                      variant={isSimracing ? "default" : "outline"}
                      className={`h-7 md:h-8 text-xs md:text-sm px-2 md:px-3 ${isSimracing 
                        ? "bg-gradient-to-r from-red-600 to-orange-600 text-white hover:from-red-700 hover:to-orange-700" 
                        : "text-orange-600 border-orange-600 hover:bg-orange-50"
                      }`}
                      data-testid="simracing-nav-btn"
                    >
                      <Gamepad2 className="w-3 h-3 md:mr-1" />
                      <span className="hidden md:inline">Simracing</span>
                    </Button>
                  </Link>
                  
                  {/* Admin Button */}
                  {isAdmin && (
                    <Link to="/admin" className="hidden md:block">
                      <Button size="sm" variant="outline" className="text-purple-600 border-purple-600 hover:bg-purple-50 h-7 md:h-8 text-xs md:text-sm px-2 md:px-3">
                        <Shield className="w-3 h-3 md:mr-1" />
                        <span className="hidden lg:inline">Admin</span>
                      </Button>
                    </Link>
                  )}
                  
                  {/* Notification Bell */}
                  <NotificationBell 
                    userId={user?.user_id}
                    onNewFilesClick={(data) => {
                      setActiveTab('races');
                      toast.info(`${data.new_files?.length || 0} archivos nuevos disponibles`);
                    }}
                  />
                  
                  <div className="flex items-center gap-1 md:gap-2 text-gray-700">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 md:w-8 md:h-8 rounded-full" />
                    ) : (
                      <User className="w-4 h-4 md:w-5 md:h-5" />
                    )}
                    <span className="text-xs md:text-sm hidden sm:inline max-w-[80px] md:max-w-none truncate">{user.name || user.email}</span>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={handleLogout}
                    className="text-gray-500 hover:text-red-600 h-7 md:h-8 px-1 md:px-2"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Card grid navigation — all options visible, click to switch */}
          {(() => {
            const NAV_ITEMS = [
              { value: 'live-telemetry', label: 'Sesiones en Vivo', Icon: Radio, desc: 'Parciales Speedhive real-time', externalRoute: '/live' },
              { value: 'races', label: 'Análisis de parciales', Icon: Clock, desc: 'Tus sesiones cargadas' },
              { value: 'favorites', label: 'Favoritos', Icon: Star, desc: 'Pilotos guardados' },
              { value: 'timer', label: 'Timer', Icon: Timer, desc: 'Box cronómetro' },
              { value: 'upload', label: 'Subir', Icon: Upload, desc: 'Archivos XLS / PDF' },
              ...(hasPitAccess
                ? [
                    { value: 'pit', label: 'Ingeniería', Icon: FileText, desc: 'Planilla de pista', externalRoute: '/pit-engineer' },
                  ]
                : []),
              ...(isPro || hasPitAccess
                ? [
                    { value: 'telemetry', label: 'Mi Telemetría', Icon: Activity, desc: 'DAQ + Replay video', externalRoute: '/telemetry' },
                  ]
                : [
                    { value: 'telemetry', label: 'Mi Telemetría', Icon: Activity, desc: 'Solo Pro — DAQ + Replay', upsell: true },
                  ]),
            ];
            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 max-w-6xl mx-auto w-full">
                {NAV_ITEMS.map(({ value, label, Icon, desc, disabled, externalRoute, upsell }) => {
                  const isActive = activeTab === value && !externalRoute;
                  return (
                    <button
                      key={value}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        if (upsell) {
                          toast.message('Función Pro', {
                            description: 'Telemetría y Replay Studio están disponibles en el plan Pro.',
                            action: { label: 'Ver planes', onClick: () => navigate('/pricing') },
                          });
                          return;
                        }
                        if (externalRoute) {
                          navigate(externalRoute);
                        } else {
                          setActiveTab(value);
                        }
                      }}
                      data-testid={`nav-card-${value}`}
                      className={`group relative flex flex-col items-start text-left gap-2 rounded-xl p-4 min-h-[118px] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500/60 ${
                        disabled
                          ? 'bg-neutral-900/40 border border-neutral-800 opacity-40 cursor-not-allowed'
                          : upsell
                            ? 'bg-gradient-to-br from-amber-950/30 to-neutral-950 border-2 border-amber-700/50 hover:border-amber-500 cursor-pointer'
                            : isActive
                              ? 'bg-gradient-to-br from-red-600 via-red-700 to-red-900 border-2 border-red-500 shadow-lg shadow-red-900/40 cursor-pointer'
                              : 'bg-gradient-to-br from-neutral-900 to-neutral-950 border-2 border-red-900/40 hover:border-red-500 hover:from-red-950/40 hover:to-neutral-900 cursor-pointer'
                      }`}
                    >
                      {upsell && (
                        <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5">
                          Pro
                        </span>
                      )}
                      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                        upsell ? 'bg-amber-500/15' : (isActive ? 'bg-white/15' : 'bg-red-600/15 group-hover:bg-red-600/25')
                      } transition-colors`}>
                        {upsell
                          ? <Lock className="h-5 w-5 text-amber-400" />
                          : <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-red-400 group-hover:text-red-300'}`} />}
                      </div>
                      <div className="flex-1">
                        <div className={`text-base md:text-lg font-bold leading-tight ${isActive ? 'text-white' : 'text-white'}`}>
                          {label}
                        </div>
                        <div className={`text-xs mt-1 ${isActive ? 'text-red-100/90' : (upsell ? 'text-amber-300/70' : 'text-gray-400')}`}>
                          {desc}
                        </div>
                      </div>
                      {isActive && (
                        <span className="text-[10px] uppercase tracking-widest font-bold text-white/90 mt-auto">
                          ● Activo
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {/* Cloud Import Tab removed from nav Feb 28 2026 — the importers
              (Speedhive Sessions Explorer + Google Drive) are now surfaced
              as an inline collapsible panel inside the Races tab under the
              "Planillas oficiales" button. */}

          {/* Favorites Tab */}
          <TabsContent value="favorites" className="space-y-6">
            <FavoritesDashboard 
              userId={user?.user_id}
              onSelectSession={(sessionId) => {
                const race = races.find(r => r.id === sessionId);
                if (race) {
                  handleSelectRace(race);
                }
              }}
            />
          </TabsContent>

          {/* Timer Tab */}
          <TabsContent value="timer" className="space-y-6">
            <Temporizador userId={user?.user_id} />
          </TabsContent>

          {/* Upload Tab */}
          <TabsContent value="upload" className="space-y-6">
            <Card className="bg-neutral-900 border-red-900/30 backdrop-blur animate-slide-in">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-2">
                  <Upload className="w-6 h-6 text-red-500" />
                  Subir Archivo de Tiempos
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Sube un archivo PDF o XLS con los tiempos de carrera
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Category Selector */}
                  <div className="bg-neutral-800/50 rounded-lg p-4 border border-red-900/30">
                    <label className="text-sm font-semibold text-white mb-3 block">
                      Categoría del Archivo
                    </label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger 
                        className="w-full bg-neutral-900 border-red-900/50 text-white"
                        data-testid="category-selector"
                      >
                        <SelectValue placeholder="Seleccionar categoría" />
                      </SelectTrigger>
                      <SelectContent className="bg-neutral-900 border-red-900/50">
                        <SelectItem value="auto" className="text-white hover:bg-red-900/30">
                          🔍 Auto-detectar formato
                        </SelectItem>
                        <SelectItem value="turismo_pista" className="text-white hover:bg-red-900/30">
                          🏎️ Turismo Pista (XLS HTML)
                        </SelectItem>
                        <SelectItem value="carshow_santafesino" className="text-white hover:bg-red-900/30">
                          🚗 CarShow Santafesino (XLS binario)
                        </SelectItem>
                        <SelectItem value="turismo_nacional" className="text-white hover:bg-red-900/30">
                          🏁 Turismo Nacional / Clase 3 (XLS binario)
                        </SelectItem>
                        <SelectItem value="rotax" className="text-white hover:bg-red-900/30" data-testid="category-option-rotax">
                          🏆 ROTAX Karting (HTML — CRONOMETRAJE SA)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-2">
                      El sistema detecta automáticamente el formato, pero puedes forzar uno específico si hay problemas.
                    </p>
                  </div>

                  <div 
                    className="border-2 border-dashed border-red-900/50 rounded-lg p-12 text-center hover:border-red-600 hover:bg-red-950/10 transition-all cursor-pointer"
                    onClick={() => document.getElementById('file-upload').click()}
                    data-testid="upload-dropzone"
                  >
                    <input
                      id="file-upload"
                      type="file"
                      accept=".pdf,.xls,.xlsx,.html"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploadProgress}
                      data-testid="file-input"
                    />
                    {uploadProgress ? (
                      <div className="space-y-3">
                        <div className="w-16 h-16 mx-auto border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
                        <p className="text-gray-200 font-medium">Procesando archivo...</p>
                        <p className="text-sm text-gray-500">Extrayendo datos</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-red-600 to-red-800 rounded-full flex items-center justify-center shadow-lg shadow-red-600/50">
                          <Upload className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-medium mb-1">Haz clic o arrastra archivo</p>
                          <p className="text-sm text-gray-400">PDF, XLS o XLSX</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-neutral-800/50 rounded-lg p-4 border border-red-900/30">
                    <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-red-500" />
                      Formatos Soportados
                    </h3>
                    <ul className="text-sm text-gray-400 space-y-1 ml-6 list-disc">
                      <li><strong>Turismo Pista (XLS/XLSX):</strong> Archivos del sistema de cronometraje TP</li>
                      <li><strong>CarShow Santafesino (XLS/PDF):</strong> Archivos del sistema CarShow</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Races Tab */}
          <TabsContent value="races">
            {showMultiSessionComparison ? (
              <div className="space-y-4">
                <Button
                  onClick={() => setShowMultiSessionComparison(false)}
                  variant="outline"
                  className="border-red-600 text-red-400 hover:bg-red-950/30"
                >
                  ← Volver a Lista de Sesiones
                </Button>
                <MultiSessionComparison
                  races={races}
                  onBack={() => setShowMultiSessionComparison(false)}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      onClick={() => setShowOfficialImport((v) => !v)}
                      variant="outline"
                      className="border-red-900/50 text-red-400 hover:bg-red-950/50 hover:text-red-300"
                      data-testid="toggle-official-import"
                    >
                      <Cloud className="w-4 h-4 mr-2" />
                      Planillas oficiales
                      <ChevronDown
                        className={`w-3.5 h-3.5 ml-2 transition-transform ${showOfficialImport ? 'rotate-180' : ''}`}
                      />
                    </Button>
                    <Button
                      onClick={() => setShowMyCategories((v) => !v)}
                      variant="outline"
                      className="border-red-900/50 text-red-400 hover:bg-red-950/50 hover:text-red-300"
                      data-testid="toggle-my-categories"
                    >
                      <Radio className="w-4 h-4 mr-2" />
                      Mis categorías MyLaps
                      <ChevronDown
                        className={`w-3.5 h-3.5 ml-2 transition-transform ${showMyCategories ? 'rotate-180' : ''}`}
                      />
                    </Button>
                    {(!!user?.is_admin || isAdmin) && (
                      <Button
                        onClick={handleClearAllRaces}
                        disabled={races.length === 0}
                        variant="outline"
                        className="border-red-900/50 text-red-400 hover:bg-red-950/50 hover:text-red-300 disabled:opacity-50"
                        data-testid="clear-all-races"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Vaciar Todo ({races.length})
                      </Button>
                    )}
                  </div>
                  <Button
                    onClick={() => {
                      if (!isPro) {
                        toast.error('Función exclusiva del plan PRO');
                        return;
                      }
                      setShowMultiSessionComparison(true);
                    }}
                    disabled={races.length < 2}
                    className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg"
                    title={races.length < 2 ? "Necesitas al menos 2 sesiones para comparar" : "Comparar progreso entre sesiones"}
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Comparar Entre Sesiones
                  </Button>
                </div>

                {showOfficialImport && (
                  <div className="rounded-lg border border-red-900/40 bg-neutral-900/60 p-4 space-y-4" data-testid="official-import-panel">
                    <div className="flex items-start gap-3">
                      <Cloud className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-gray-300">
                        <p className="font-semibold text-white mb-1">Planillas oficiales</p>
                        <p className="text-gray-400 text-xs">
                          Archivos vuelta-por-vuelta (VTAxVTA) y planillas oficiales
                          publicados por la categoría en Google Drive.
                        </p>
                      </div>
                    </div>
                    <DriveImporter
                      onImportSuccess={() => {
                        fetchRaces();
                        setShowOfficialImport(false);
                      }}
                    />
                  </div>
                )}

                {showMyCategories && (
                  <MyCategoriesManager isPro={isPro} />
                )}

                <RaceList
                  races={races}
                  racesLoaded={racesLoaded}
                  onSelectRace={handleSelectRace}
                  onDeleteRace={handleDeleteRace}
                  onRacesChanged={fetchRaces}
                  loading={loading}
                  canDelete={!!user?.is_admin || isAdmin}
                />
              </div>
            )}
          </TabsContent>

          {/* Compare Tab */}
          <TabsContent value="compare">
            {selectedRace ? (
              <RaceComparison race={selectedRace} isPro={isPro} userId={user?.user_id} />
            ) : (
              <Card className="bg-neutral-900 border-red-900/30">
                <CardContent className="py-12 text-center">
                  <Users className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                  <p className="text-gray-400">Selecciona una carrera para analizar</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;